
import React, { useState, useEffect } from 'react';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Button } from './components/Button';
import { ContactModal } from './components/ContactModal';
import { AndorraBanner } from './components/AndorraBanner';
import { TicketIcon, MagnifyingGlassIcon, GavelIcon } from './components/IconComponents';
import { PrivacyPolicyModal } from './components/PrivacyPolicyModal';
import { CookiePolicyModal } from './components/CookiePolicyModal';
import { LegalNoticeModal } from './components/LegalNoticeModal';
import { AboutUsModal } from './components/AboutUsModal';
import { MultasEuModal } from './components/MultasEuModal';
import { PeritajesEuModal } from './components/PeritajesEuModal';
import { JuzgadosEuModal } from './components/JuzgadosEuModal';
import Chatbot from './components/Chatbot';


interface SectionWithModalTriggerProps {
  onOpenModal: () => void;
}

const HeroSection: React.FC<SectionWithModalTriggerProps> = ({ onOpenModal }) => {
  // In a standard build, files in public/ are served from root.
  const imageUrl = '/compa-ia-que-defiende-s peritajes.EU.png';

  const heroStyle = {
    backgroundImage: `url('${imageUrl}')`
  };
  
  return (
    <section 
      className="bg-cover bg-center text-white relative"
      style={heroStyle}
    >
      <div className="absolute inset-0 bg-black opacity-40"></div>
      <div className="container mx-auto px-6 py-20 md:py-32 text-center relative z-10">
        <h1 className="text-4xl md:text-6xl font-bold mb-4">
          Nunca más volverás a estar sol@ ante las autoridades.
        </h1>
        <p className="text-xl md:text-2xl mb-6">
          Fuimos Multas.com. Crecimos como Multas.org. Ahora, somos <span className="font-bold text-yellow-400">Peritajes.EU</span> y <span className="font-bold text-blue-400">Multas.EU</span>.
        </p>
        <p className="text-lg md:text-xl mb-10 max-w-3xl mx-auto">
          Llevamos nuestra experiencia en defensa del ciudadano a toda Europa, ofreciéndote una protección legal sin fronteras.
        </p>
        <div className="flex flex-col items-center gap-4">
          <Button onClick={onOpenModal} size="large" variant="primary">
            CONSULTA TU CASO AHORA
          </Button>
          <a href="https://multas.eu" target="_blank" rel="noopener noreferrer">
            <Button size="large" variant="success">
              Visitar la Pagina Principal del Servicio, Multas.EU
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  theme: 'blue' | 'yellow' | 'red';
  onClick?: () => void;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, description, theme, onClick }) => {
  const isClickable = !!onClick;
  const cardClasses = `bg-white p-8 rounded-xl shadow-xl transition-all duration-300 text-center flex flex-col items-center h-full ${
    isClickable ? 'hover:shadow-2xl hover:-translate-y-2 cursor-pointer' : 'hover:shadow-2xl'
  }`;

  const themeStyles = {
    blue: {
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600',
    },
    yellow: {
      iconBg: 'bg-yellow-100',
      iconText: 'text-yellow-600',
    },
    red: {
      iconBg: 'bg-red-100',
      iconText: 'text-red-600',
    },
  };
  
  const currentTheme = themeStyles[theme];
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      className={cardClasses}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      tabIndex={isClickable ? 0 : -1}
      role={isClickable ? 'button' : undefined}
      aria-label={isClickable ? `Aprender más sobre ${title}` : undefined}
    >
      <div className={`w-24 h-24 rounded-full flex items-center justify-center mb-6 ${currentTheme.iconBg}`}>
        <div className={currentTheme.iconText}>{icon}</div>
      </div>
      <h3 className="text-xl font-semibold text-gray-800 mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

interface ServicesSectionProps {
  onOpenMultasEuModal: () => void;
  onOpenPeritajesEuModal: () => void;
  onOpenJuzgadosEuModal: () => void;
}

const ServicesSection: React.FC<ServicesSectionProps> = ({ 
  onOpenMultasEuModal, 
  onOpenPeritajesEuModal, 
  onOpenJuzgadosEuModal 
}) => {
  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-6">
        <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-4">
          Una cobertura 360º para tu total tranquilidad.
        </h2>
        <p className="text-lg text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          Con Peritajes.EU y Multas.EU, hemos integrado nuevos servicios para ofrecerte un escudo legal completo. Ya no necesitas buscar en varios sitios, lo tienes todo aquí.
        </p>
        <div className="grid md:grid-cols-3 gap-8">
          <ServiceCard
            icon={<MagnifyingGlassIcon className="w-16 h-16" />}
            title="Peritajes.EU - Informes de Expertos"
            description="¿Necesitas un informe pericial para un accidente o reconstrucción de hechos? Nuestro equipo de peritos te proporciona la prueba técnica que necesitas para ganar."
            onClick={onOpenPeritajesEuModal}
            theme="yellow"
          />
          <ServiceCard
            icon={<TicketIcon className="w-16 h-16" />}
            title="Multas.EU - Defensa de Sanciones"
            description="Recurrimos y gestionamos tus multas de tráfico, velocidad, aparcamiento, alcoholemia y más. Expertos en la legislación de cada país europeo."
            onClick={onOpenMultasEuModal}
            theme="blue"
          />
          <ServiceCard
            icon={<GavelIcon className="w-16 h-16" />}
            title="Juzgados.EU - Representación Legal"
            description="Si tu caso requiere llegar a la vía judicial, nuestros abogados especialistas te representarán con firmeza y profesionalidad en los juzgados."
            onClick={onOpenJuzgadosEuModal}
            theme="red"
          />
        </div>
      </div>
    </section>
  );
};

const QualitySealSection: React.FC = () => {
  return (
    <section className="py-16 bg-gray-800 text-white">
      <div className="container mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-yellow-400">
          SERVICIO PREMIUM AAA+
        </h2>
        <p className="text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
          No somos un simple proveedor de informes. Somos tu equipo legal personal. Nuestro compromiso es ofrecerte un servicio de máxima calidad, con transparencia, comunicación constante y un único objetivo: proteger tus derechos y tu bolsillo. Si, porque aunque nuestros servicios sean Premium, nuestros precios son Baratos
        </p>
      </div>
    </section>
  );
};

const FinalCTASection: React.FC<SectionWithModalTriggerProps> = ({ onOpenModal }) => {
  return (
    <section className="py-20 bg-blue-600 text-white">
      <div className="container mx-auto px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-4">
          ¿Tienes algun problema Administrativo o Legal en Europa? <br /> NO TE LA JUEGUES.
        </h2>
        <p className="text-lg md:text-xl mb-6">
          Cada minuto cuenta. Ponte en manos de los expertos que han revolucionado la defensa del ciudadano.
        </p>
        <p className="text-lg md:text-xl mb-10">
          Déjanos analizar tu caso sin compromiso.
        </p>
        <div className="flex flex-col items-center gap-4">
          <Button onClick={onOpenModal} size="large" variant="secondary">
            PROTEGER MIS DERECHOS AHORA
          </Button>
          <a href="https://multas.eu" target="_blank" rel="noopener noreferrer">
            <Button size="large" variant="success">
              Visitar la Pagina Principal del Servicio, Multas.EU
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
};

const App: React.FC = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);
  const [isCookieModalOpen, setIsCookieModalOpen] = useState(false);
  const [isLegalNoticeModalOpen, setIsLegalNoticeModalOpen] = useState(false);
  const [isAboutUsModalOpen, setIsAboutUsModalOpen] = useState(false);
  const [isMultasEuModalOpen, setIsMultasEuModalOpen] = useState(false);
  const [isPeritajesEuModalOpen, setIsPeritajesEuModalOpen] = useState(false);
  const [isJuzgadosEuModalOpen, setIsJuzgadosEuModalOpen] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('form_submitted') === 'true') {
        setShowSuccessBanner(true);
        window.history.replaceState({}, document.title, window.location.pathname); // Clean URL
        const timer = setTimeout(() => setShowSuccessBanner(false), 6000); // Hide after 6s
        return () => clearTimeout(timer);
    }
  }, []);

  const handleOpenContactModal = () => setIsContactModalOpen(true);
  const handleCloseContactModal = () => setIsContactModalOpen(false);

  const handleOpenPrivacyModal = () => setIsPrivacyModalOpen(true);
  const handleClosePrivacyModal = () => setIsPrivacyModalOpen(false);

  const handleOpenCookieModal = () => setIsCookieModalOpen(true);
  const handleCloseCookieModal = () => setIsCookieModalOpen(false);

  const handleOpenLegalNoticeModal = () => setIsLegalNoticeModalOpen(true);
  const handleCloseLegalNoticeModal = () => setIsLegalNoticeModalOpen(false);

  const handleOpenAboutUsModal = () => setIsAboutUsModalOpen(true);
  const handleCloseAboutUsModal = () => setIsAboutUsModalOpen(false);

  const handleOpenMultasEuModal = () => setIsMultasEuModalOpen(true);
  const handleCloseMultasEuModal = () => setIsMultasEuModalOpen(false);
  
  const handleOpenPeritajesEuModal = () => setIsPeritajesEuModalOpen(true);
  const handleClosePeritajesEuModal = () => setIsPeritajesEuModalOpen(false);
  
  const handleOpenJuzgadosEuModal = () => setIsJuzgadosEuModalOpen(true);
  const handleCloseJuzgadosEuModal = () => setIsJuzgadosEuModalOpen(false);

  return (
    <div className="flex flex-col min-h-screen bg-gray-100 text-gray-800">
       {showSuccessBanner && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 w-11/12 max-w-lg bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg shadow-lg z-[100]" role="alert">
          <div className="flex items-center">
              <svg className="w-6 h-6 mr-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
              <div>
                  <strong className="font-bold">¡Gracias!</strong>
                  <span className="block sm:inline ml-1">Tu formulario ha sido enviado correctamente.</span>
              </div>
          </div>
          <button className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setShowSuccessBanner(false)}>
              <svg className="fill-current h-6 w-6 text-green-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><title>Cerrar</title><path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/></svg>
          </button>
        </div>
      )}
      <Header />
      <AndorraBanner />
      <main className="flex-grow">
        <HeroSection onOpenModal={handleOpenContactModal} />
        <ServicesSection 
          onOpenMultasEuModal={handleOpenMultasEuModal}
          onOpenPeritajesEuModal={handleOpenPeritajesEuModal}
          onOpenJuzgadosEuModal={handleOpenJuzgadosEuModal}
        />
        <QualitySealSection />
        <FinalCTASection onOpenModal={handleOpenContactModal} />
      </main>
      <Footer 
        onOpenPrivacyModal={handleOpenPrivacyModal}
        onOpenCookieModal={handleOpenCookieModal}
        onOpenLegalNoticeModal={handleOpenLegalNoticeModal}
        onOpenAboutUsModal={handleOpenAboutUsModal}
      />
      <ContactModal 
        isOpen={isContactModalOpen} 
        onClose={handleCloseContactModal}
        onOpenPrivacyModal={handleOpenPrivacyModal}
        onOpenCookieModal={handleOpenCookieModal}
      />
      <PrivacyPolicyModal isOpen={isPrivacyModalOpen} onClose={handleClosePrivacyModal} />
      <CookiePolicyModal isOpen={isCookieModalOpen} onClose={handleCloseCookieModal} />
      <LegalNoticeModal isOpen={isLegalNoticeModalOpen} onClose={handleCloseLegalNoticeModal} />
      <AboutUsModal isOpen={isAboutUsModalOpen} onClose={handleCloseAboutUsModal} />
      <MultasEuModal 
        isOpen={isMultasEuModalOpen} 
        onClose={handleCloseMultasEuModal} 
        onOpenContactModal={handleOpenContactModal}
      />
      <PeritajesEuModal 
        isOpen={isPeritajesEuModalOpen} 
        onClose={handleClosePeritajesEuModal} 
        onOpenContactModal={handleOpenContactModal}
      />
      <JuzgadosEuModal 
        isOpen={isJuzgadosEuModalOpen} 
        onClose={handleCloseJuzgadosEuModal} 
        onOpenContactModal={handleOpenContactModal}
      />
      <Chatbot />
    </div>
  );
};

export default App;
